import React, { useEffect, useState } from 'react';
import { Col, Card, CardBody, CardHeader, ListGroup, Container, Row } from 'shards-react';
import PageTitle from '../components/common/PageTitle';
import Notification from '../components/common/Notification';
import DownloadItem from '../components/download/DownloadItem';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useLocales from '../hooks/useLocales';
import useUserRoles from '../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../redux/store';
import { getOwnInfo } from '../redux/slices/ownInfo';
import getRole from '../utils/getRole';
import { LANGUAGE } from '../constants';
import type { Role } from '../types/common';
import type { OrganizationUserType } from '../types/user';

/**
 * 【参考】資料が商社共通か、商社ごとか（商社ごとに別のファイルを用意する場合はS3の商社ファオルダ配下に置く）
 * 　・ユーザーマニュアル（共通）
 * 　・管理マニュアル（共通）
 * 　・利用規約（商社別）
 * 　・利用申請書_社外用（共通）
 * 　・利用申請書_社内用（商社別）
 * 　・利用規約承諾書（商社別）
 */
/**
 * 【参考】どのロールで何がDLできるか（User, Poweruser, Admin, Maintenanceadmin）
 * 　・ユーザーマニュアル：U, P, A, M
 * 　・管理マニュアル：P, A, M
 * 　・利用規約：U, P, A
 * 　・利用申請書_社外用：U, P, A, M
 * 　・利用申請書_社内用：P, A
 * 　・利用規約承諾書：U, P, A
 */
const Download = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentLang } = useLocales();
  const { isAdminOrPoweruser, isMaintenanceadmin } = useUserRoles();
  const { ownInfo } = useSelector((state: RootState) => state.ownInfo);
  const { roles } = useSelector((state: RootState) => state.session);
  const role: Role = getRole(roles);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUserType[]>([]);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);
  useEffect(() => {
    dispatch(getOwnInfo());
  }, []);

  /**
   * User情報が取得できたら商社コードをセットする
   */
  useEffect(() => {
    if (ownInfo !== undefined) {
      setOrganizationUsers(ownInfo.organizations);
    }
  }, [ownInfo]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * クローズボタンを押したとき
   */
  const dismiss = (): void => {
    setMessage(undefined);
  };

  // VIEW
  // ================================================================================

  return (
    <Container fluid className="main-content-container px-4">
      <Row id="pageTitle" noGutters className="page-header py-4">
        <PageTitle sm="4" title={t('helpCenter.download.title')} subtitle={t('helpCenter.title')} className="text-sm-left" />
      </Row>

      <Notification theme={'danger'} isOpen={message !== undefined && message.length > 0} message={message} dismissCallback={dismiss} />

      <Row>
        {/* マニュアル
        ======================================== */}
        <Col md={6}>
          <Card small className="mb-4">
            <CardHeader className="border-bottom">
              <h6 className="m-0">{t('helpCenter.download.manual.title')}</h6>
            </CardHeader>
            <CardBody className="p-4">
              <ListGroup small flush className="list-group-small">
                {/* ユーザーマニュアル（共通）
                ---------------------------------------- */}
                <DownloadItem
                  displayName={t('helpCenter.download.manual.user')}
                  path={'documents/' + role + '/timss_users_manual.pdf'}
                  fileName={'timss_users_manual.pdf'}
                  icon={faFilePdf}
                  iconClassName={'text-salmon'}
                  setErrorMessage={setMessage}
                />
                {/* 管理マニュアル（共通）
                ---------------------------------------- */}
                {(isAdminOrPoweruser || isMaintenanceadmin) && (
                  <DownloadItem
                    displayName={t('helpCenter.download.manual.admin')}
                    path={'documents/' + role + '/timss_admin_manual.pdf'}
                    fileName={'timss_admin_manual.pdf'}
                    icon={faFilePdf}
                    iconClassName={'text-salmon'}
                    setErrorMessage={setMessage}
                  />
                )}
              </ListGroup>
            </CardBody>
          </Card>
        </Col>

        {/* 利用申請
        ======================================== */}
        <Col md={6}>
          <Card small className="mb-4">
            <CardHeader className="border-bottom">
              <h6 className="m-0">{t('helpCenter.download.application.title')}</h6>
            </CardHeader>
            <CardBody className="p-4">
              <ListGroup small flush className="list-group-small">
                {/* 利用規約（商社別）
                ---------------------------------------- */}
                {organizationUsers.length > 0 &&
                  organizationUsers.map((orgUser, index) => (
                    <DownloadItem
                      key={'timss-terms-of-use-' + index}
                      displayName={t('helpCenter.download.application.termsOfUse') + '（' + (currentLang.value === LANGUAGE.JA ? orgUser.tradingCompanyNameJa : orgUser.tradingCompanyNameEn) + '）'}
                      path={orgUser.tradingCompanyCode + '/documents/' + role + '/timss_terms_of_use.pdf'}
                      fileName={'timss_terms_of_use_' + orgUser.tradingCompanyCode + '.pdf'}
                      icon={faFilePdf}
                      iconClassName={'text-salmon'}
                      setErrorMessage={setMessage}
                    />
                  ))}
                {/* 利用規約承諾書（商社別）
                  ---------------------------------------- */}
                {organizationUsers.length > 0 &&
                  organizationUsers.map((orgUser, index) => (
                    <DownloadItem
                      key={'timss-acceptance-form-' + index}
                      displayName={t('helpCenter.download.application.agreement') + '（' + (currentLang.value === LANGUAGE.JA ? orgUser.tradingCompanyNameJa : orgUser.tradingCompanyNameEn) + '）'}
                      path={orgUser.tradingCompanyCode + '/documents/' + role + '/timss_acceptance_form.xlsx'}
                      fileName={'timss_agreement_form_' + orgUser.tradingCompanyCode + '.xlsx'}
                      icon={faFileExcel}
                      iconClassName={'text-java'}
                      setErrorMessage={setMessage}
                    />
                  ))}
                {/* 利用申請書_社外用（共通）
                ---------------------------------------- */}
                <DownloadItem
                  displayName={t('helpCenter.download.application.applicationExternal')}
                  path={'documents/' + role + '/timss_application_form_external.xlsx'}
                  fileName={'timss_application_form_external.xlsx'}
                  icon={faFileExcel}
                  iconClassName={'text-java'}
                  setErrorMessage={setMessage}
                />
                {/* 利用申請書_社内用（商社別）
                ---------------------------------------- */}
                {isAdminOrPoweruser &&
                  organizationUsers.length > 0 &&
                  organizationUsers.map((orgUser, index) => (
                    <DownloadItem
                      key={'timss-application-form-internal-' + index}
                      displayName={t('helpCenter.download.application.applicationInternal')}
                      path={orgUser.tradingCompanyCode + '/documents/' + role + '/timss_application_form_internal.xlsx'}
                      fileName={'timss_application_form_internal_' + orgUser.tradingCompanyCode + '.xlsx'}
                      icon={faFileExcel}
                      iconClassName={'text-java'}
                      setErrorMessage={setMessage}
                    />
                  ))}
              </ListGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Download;
